/* eslint-disable vue/multi-word-component-names */

import {createApp} from 'vue'
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import smoothScroll from './directives/smoothScroll';
import { definePreset } from '@primeuix/themes';
import * as Sentry from "@sentry/vue";

// Import PrimeVue CSS

// Import App and stores
import App from './App.vue'
import router from './router'
import store from './store'
import ToastService from 'primevue/toastservice'
import DialogService from 'primevue/dialogservice'
import 'primeicons/primeicons.css'
import ConfirmDialog from 'primevue/confirmdialog';

// Import Quill CSS for PrimeVue Editor
import 'quill/dist/quill.snow.css';


// Import PrimeVue components
import Tooltip from 'primevue/tooltip';
import FloatLabel from 'primevue/floatlabel';
import Badge from 'primevue/badge';
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Card from 'primevue/card'
import FileUpload from 'primevue/fileupload'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import ProgressBar from 'primevue/progressbar'
import Toast from 'primevue/toast'
import Dialog from 'primevue/dialog'
import Menu from 'primevue/menu'
import Tag from 'primevue/tag'
import Divider from 'primevue/divider'
import Select from 'primevue/select'
import ProgressSpinner from 'primevue/progressspinner'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import ButtonGroup from 'primevue/buttongroup';
import Message from 'primevue/message';
import ColumnGroup from 'primevue/columngroup';   // optional
import Row from 'primevue/row';                   // optional
import Password from 'primevue/password';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';   //Optional for grouping
import Ripple from 'primevue/ripple';
import InputNumber from 'primevue/inputnumber';
import RadioButton from 'primevue/radiobutton';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Textarea from 'primevue/textarea';
import DatePicker from 'primevue/datepicker';
import Knob from 'primevue/knob';
import Checkbox from 'primevue/checkbox';
import Chip from 'primevue/chip';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import Slider from 'primevue/slider';
import MultiSelect from 'primevue/multiselect';
import Drawer from 'primevue/drawer';
import Chart from 'primevue/chart';
import SelectButton from 'primevue/selectbutton';
import AutoComplete from 'primevue/autocomplete';
import Image from 'primevue/image';
import Panel from 'primevue/panel';
import Editor from 'primevue/editor';
import TieredMenu from 'primevue/tieredmenu';




// Create and mount app
const app = createApp(App);

Sentry.init({
    app,
    dsn: "https://41fdf2bc1a27e729d7a9916f20a6248a@o4505585468178432.ingest.us.sentry.io/4509416254537728",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true
});
import ConfirmationService from 'primevue/confirmationservice';
app.use(ConfirmationService);



// Add auth unauthorized event listener
window.addEventListener('auth:unauthorized', () => {
    // Logout the user when token is unauthorized
    store.dispatch('logoutAndRedirect')

})

const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{slate.50}',
            100: '{slate.100}',
            200: '{slate.200}',
            300: '{slate.300}',
            400: '{slate.400}',
            500: '#1D262B',
            600: '{slate.600}',
            700: '{slate.700}',
            800: '{slate.800}',
            900: '{slate.900}',
            950: '{slate.950}'
        },
        colorScheme: {
            light: {
                surface: {
                    0: '#ffffff',
                    50: '#fdfdfd',
                    100: '{zinc.100}',
                    200: '{zinc.200}',
                    300: '{zinc.300}',
                    400: '{zinc.400}',
                    500: '#1D262B',
                    600: '{zinc.600}',
                    700: '#1D262B',
                    800: '#1D262B',
                    900: '{zinc.900}',
                    950: '{zinc.950}'
                }
            },
            dark: {
                surface: {
                    0: '#ffffff',
                    50: '{slate.50}',
                    100: '{slate.100}',
                    200: '{slate.200}',
                    300: '{slate.300}',
                    400: '{slate.400}',
                    500: '{slate.500}',
                    600: '{slate.600}',
                    700: '{slate.700}',
                    800: '{slate.800}',
                    900: '{slate.900}',
                    950: '{slate.950}'
                }
            }
        }
    }
});

// Use plugins
app.use(store)
   .use(router)
   .use(PrimeVue, {
      theme: {
          preset: MyPreset,
          options: {
              darkModeSelector: '.fake-dark-selector', // trying to also force a non-usage of the dark mode
          },
      },
      ripple: true,
      inputStyle: 'filled'
   })
   .directive('smooth-scroll', smoothScroll)

app.use(ToastService)
window.$toast = app.config.globalProperties.$toast


app.use(DialogService)

// Register PrimeVue components
app.component('Button', Button)
app.component('InputText', InputText)
app.component('DataTable', DataTable)
app.component('Column', Column)
app.component('Card', Card)
app.component('FileUpload', FileUpload)
app.component('Tabs', Tabs)
app.component('TabList', TabList)
app.component('Tab', Tab)
app.component('TabPanels', TabPanels)
app.component('TabPanel', TabPanel)
app.component('ProgressBar', ProgressBar)
app.component('Toast', Toast)
app.component('ConfirmDialog', ConfirmDialog)
app.component('Dialog', Dialog)
app.component('Menu', Menu)
app.component('Tag', Tag)
app.component('Select', Select)
app.component('ProgressSpinner', ProgressSpinner)
app.component('FloatLabel', FloatLabel)
app.component('Badge', Badge)
app.component('IconField', IconField)
app.component('InputIcon', InputIcon)
app.component('ButtonGroup', ButtonGroup)
app.component('Message', Message)
app.component('Password', Password);
app.component('Avatar', Avatar);
app.component('AvatarGroup', AvatarGroup);
app.component('ColumnGroup', ColumnGroup);
app.component('Row', Row);
app.component('InputNumber', InputNumber);
app.component('RadioButton', RadioButton);
app.component('Accordion', Accordion);
app.component('AccordionPanel', AccordionPanel);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionContent', AccordionContent);
app.component('Textarea', Textarea);
app.component('DatePicker', DatePicker);
app.component('Divider', Divider);
app.component('Knob', Knob);
app.component('Checkbox', Checkbox);
app.component('Chip', Chip);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('FilterMatchMode', FilterMatchMode);
app.component('FilterOperator', FilterOperator);
app.component('Slider', Slider);
app.component('MultiSelect', MultiSelect);
app.component('Drawer', Drawer);
app.component('Chart', Chart);
app.component('SelectButton', SelectButton);
app.component('AutoComplete', AutoComplete);
app.component('Image', Image);
app.component('Panel', Panel);
app.component('Editor', Editor);
app.component('TieredMenu', TieredMenu);




app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);

app.mount('#app')


